/**
 * QueuedMessageBar — shows pending messages waiting to be sent.
 *
 * Appears above the composer while messages are waiting to be sent. Each item can be
 * edited inline or removed before it is sent. The message already in flight is not
 * listed: it is in the transcript as an optimistic bubble, and listing it again made
 * the bar flash "Waiting for twins..." for the length of every send request.
 */

import { useState, useRef, useEffect } from 'react'
import { X, Pencil, Check } from 'lucide-react'
import type { QueueBlockedReason, QueuedMessage } from '../useChatRoom'

interface QueuedMessageBarProps {
  queue: Array<QueuedMessage>
  isSending: boolean
  /** Why the queue cannot drain, from useChatRoom. Null means it can. */
  blockedReason?: QueueBlockedReason | null
  onEdit: (id: string, content: string) => void
  onRemove: (id: string) => void
  onRetry?: (id: string) => void
  blockedQueuedId?: string | null
}

interface ItemProps {
  item: QueuedMessage
  onEdit: (id: string, content: string) => void
  onRemove: (id: string) => void
}

function QueueItem(props: ItemProps) {
  const { item, onEdit, onRemove } = props
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(item.content)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.selectionStart = textareaRef.current.value.length
    }
  }, [editing])

  // Keep draft in sync if the queue item is mutated externally.
  useEffect(() => {
    if (!editing) setDraft(item.content)
  }, [item.content, editing])

  function commitEdit() {
    const trimmed = draft.trim()
    if (!trimmed) {
      setEditing(false)
      return
    }
    // An unchanged conflict must still reach onEdit. That id is already bound to
    // another payload, and confirming the edit is how the client mints a new one.
    const unchanged = trimmed === item.content
    if (!unchanged || item.recoveryReason === 'client_request_conflict') {
      onEdit(item.id, trimmed)
    }
    setEditing(false)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      commitEdit()
    }
    if (e.key === 'Escape') {
      setDraft(item.content)
      setEditing(false)
    }
  }

  return (
    <div
      className="flex items-start gap-2 rounded-xl bg-twin-chat-bubble/25 px-3 py-2 text-sm text-twin-chat-fg-muted transition-colors"
      data-testid={`queued-message-${item.id}`}
    >
      {/* Status indicator */}
      <span className="mt-0.5 shrink-0">
        <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full border border-twin-chat-fg-muted/50 text-[9px] font-semibold text-twin-chat-fg-muted">
          {/* queue position dot */}
        </span>
      </span>

      {/* Content / editable area */}
      <div className="min-w-0 flex-1">
        {editing ? (
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={4000}
            onBlur={commitEdit}
            rows={1}
            className="w-full resize-none bg-transparent text-sm text-twin-chat-fg outline-none placeholder:text-twin-chat-fg-muted"
            data-testid={`queued-message-edit-input-${item.id}`}
          />
        ) : (
          <span className="line-clamp-2 break-words leading-snug">{item.content}</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1">
        {editing ? (
          <button
            type="button"
            onClick={commitEdit}
            className="rounded p-0.5 text-twin-chat-send hover:bg-twin-chat-send/15 transition-colors"
            aria-label="Confirm edit"
            data-testid={`queued-message-confirm-${item.id}`}
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded p-0.5 text-twin-chat-fg-muted hover:text-twin-chat-fg hover:bg-twin-chat-bubble/40 transition-colors"
            aria-label="Edit queued message"
            data-testid={`queued-message-edit-${item.id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          className="rounded p-0.5 text-twin-chat-fg-muted hover:text-destructive hover:bg-destructive/10 transition-colors"
          aria-label="Remove queued message"
          data-testid={`queued-message-remove-${item.id}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

export function QueuedMessageBar(props: QueuedMessageBarProps) {
  const { queue, isSending, blockedReason = null, onEdit, onRemove, blockedQueuedId, onRetry } = props

  // The first item while a send is in flight is already in the transcript, so only
  // messages still waiting their turn are listed. A blocked item stays: it needs the user.
  const waiting = queue.filter((item, idx) => !(idx === 0 && isSending && item.id !== blockedQueuedId))

  if (waiting.length === 0) return null

  // Say why it is waiting whenever it really is waiting. Keying this off the 409
  // flag alone read "1 Queued" through an entire turn the message could not be
  // sent in. A send that failed outranks waiting: it needs the user, not time.
  const statusLabel = blockedQueuedId
    ? 'Needs attention'
    : blockedReason
      ? 'Waiting for twins...'
      : waiting.length === 1 ? '1 queued' : `${waiting.length} queued`

  return (
    <div
      className="mx-4 mb-2 flex flex-col gap-1.5 rounded-2xl border border-twin-chat-divider/30 bg-twin-chat-surface/70 p-2 backdrop-blur-sm"
      data-testid="queued-message-bar"
    >
      <p className="px-1 text-xs font-medium text-twin-chat-fg-muted">
        {statusLabel}
      </p>
      {blockedQueuedId && onRetry && (
        <button
          type="button"
          onClick={() => onRetry(blockedQueuedId)}
          disabled={isSending}
          data-testid={`queued-message-retry-${blockedQueuedId}`}
          className="self-start rounded-md px-3 py-1.5 text-sm font-medium text-twin-chat-fg hover:bg-twin-chat-bubble/40 disabled:opacity-50"
        >
          Retry message
        </button>
      )}
      {waiting.map((item) => (
        <QueueItem
          key={item.id}
          item={item}
          onEdit={onEdit}
          onRemove={onRemove}
        />
      ))}
    </div>
  )
}
